import clsx from 'clsx'
import React, { FC, useContext, useEffect, useState } from 'react'
import { useCookies } from 'react-cookie'
import styled from 'styled-components'

import BagIcon from '@Assets/SVG/Bag'
import HeartIcon from '@Assets/SVG/Heart'
import UserIcon from '@Assets/SVG/User'
import { Abundance } from '@Components/Badges/Abundance'
import { CountryDetectionModal } from '@Components/CountryDetectionModal'
import { LanguageToggle } from '@Components/Header/LanguageToggle'
import { IconWithLabel, SVGIcon } from '@Components/Icons'
import { Link } from '@Components/Link'
import { LoginModals } from '@Components/Login'
import { SearchInput } from '@Components/Search/Desktop/Input'
import { useWishList } from '@Components/WishList/useWishList'
import { NavigationEvent } from '@Events/Navigation'
import { MetadataContext } from '@Globals/Metadata'
import { up } from '@Globals/Style/breakpoints'
import { sectionInner, sectionOuter } from '@Globals/Style/mixins'
import { useTranslation } from '@Globals/Translations'
import { UserContext } from '@Globals/User'

interface MainBarProps {
  hide: boolean
}

const WEB_CRAWLERS_REGEX = /bot|google|baidu|bing|msn|duckduckbot|teoma|slurp|yandex/i

const MainBarWrapper = styled.div`
  ${sectionOuter}
  &.hide {
    display: none;
  }

  .mainBarInner {
    ${sectionInner}
    display: flex;
    align-items: center;
    justify-content: center;
    padding-top: 15px;
    padding-bottom: 7px;

    ${up('desktop')} {
      padding-top: 0;
      padding-bottom: 0;
      height: 80px;
      justify-content: space-between;
    }
  }

  .logo img {
    height: 32px;
    width: 150px;

    ${up('desktop')} {
      height: 36px;
      margin-top: 4px;
      width: 170px;
    }
  }

  .rightSide {
    align-items: center;
    justify-content: flex-end;
    width: 260px;
    transform: translateX(15px);
    display: none;

    ${up('desktop')} {
      margin-top: -5px;
      display: flex;
    }
  }

  .icon {
    flex-shrink: 0;
    width: 80px;
    padding: 15px 0;
    transform: translateY(4px);

    &.button {
      background-color: transparent;
      border: none;
    }
  }
`

export const MainBar: FC<MainBarProps> = (
  {
    hide,
  }: MainBarProps,
) => {
  const { t } = useTranslation()

  const { itemsInWishlist } = useWishList()
  const [cookies, setCookie] = useCookies()
  const [loginModalOpen, setLoginModalOpen] = useState(false)
  const [countryDetectionModalOpen, setCountryDetectionModalOpen] = useState(false)

  const { store, storeURL } = useContext(MetadataContext)
  const country = store?.config?.countryCode

  const accountURL = storeURL('customer/account')
  const cartURL = storeURL('checkout/cart/')
  const homeURL = storeURL('')
  const wishListURL = storeURL('account/wishlist/')

  const { userData } = useContext(UserContext)

  useEffect(() => {
    if (!userData?.geoIPCountryCode) {
      return
    }

    // Check for crawlers
    if (window.navigator && WEB_CRAWLERS_REGEX.test(window.navigator.userAgent)) {
      return
    }

    const currentCookieValue = cookies.th_country
    if (country === currentCookieValue) {
      return
    }
    setCookie('th_country', country)

    if (country === userData?.geoIPCountryCode) {
      return
    }

    setCountryDetectionModalOpen(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.geoIPCountryCode])

  return (
    <MainBarWrapper className={clsx({ hide })}>
      {userData?.geoIPCountryCode && (
        <CountryDetectionModal
          geoIPCountryCode={userData.geoIPCountryCode}
          isOpen={countryDetectionModalOpen}
          onHide={() => setCountryDetectionModalOpen(false)}
        />
      )}
      <div className="mainBarInner">
        {/* Search */}
        <SearchInput id="th-main-bar-search" />

        {/* Logo */}
        <Link
          className="logo"
          event={new NavigationEvent({
            attributes: {
              dest_page_type: 'frontpages',
              dest_page_type_id: '',
              source_id: 'header',
            },
            eventName: 'nav_link_click',
          })}
          href={homeURL}
        >
          <img
            alt="Trendhim"
            src="https://res.cloudinary.com/trendhim/image/upload/v1570536495/frontend/trendhim-logo-with-text.svg?v3"
          />
        </Link>

        {/* Right side */}
        <div className="rightSide">
          {/* Language toggle */}
          <LanguageToggle />

          {/* User account */}
          {userData?.loggedIn && (
            <Link
              className="icon"
              event={new NavigationEvent({
                attributes: {
                  dest_page_type: 'customer-area',
                  dest_page_type_id: '',
                  source_id: 'header',
                },
                eventName: 'nav_link_click',
              })}
              href={accountURL}
            >
              <IconWithLabel label={t('header:MY_ACCOUNT_LABEL')}>
                <SVGIcon icon={UserIcon} />
              </IconWithLabel>
            </Link>
          )}
          {!userData?.loggedIn && (
            <button
              className="icon button"
              onClick={() => setLoginModalOpen(true)}
              type="button"
            >
              <IconWithLabel label={t('header:LOG_IN_LABEL')}>
                <SVGIcon icon={UserIcon} />
              </IconWithLabel>
            </button>
          )}

          {/* Wish list */}
          <Link
            className="icon"
            event={new NavigationEvent({
              attributes: {
                dest_page_type: 'wish-list',
                dest_page_type_id: '',
                source_id: 'header',
              },
              eventName: 'nav_link_click',
            })}
            href={wishListURL}
          >
            <IconWithLabel label={t('header:WISH_LIST_LABEL')}>
              <Abundance count={itemsInWishlist}>
                <SVGIcon icon={HeartIcon} />
              </Abundance>
            </IconWithLabel>
          </Link>

          {/* Cart */}
          <Link
            className="icon"
            event={new NavigationEvent({
              attributes: {
                dest_page_type: 'cart',
                dest_page_type_id: '',
                source_id: 'header',
              },
              eventName: 'nav_link_click',
            })}
            href={cartURL}
          >
            <IconWithLabel label={t('header:CART_SHORT_LABEL')}>
              <Abundance count={userData?.basket?.quantity}>
                <SVGIcon icon={BagIcon} />
              </Abundance>
            </IconWithLabel>
          </Link>
        </div>
      </div>
      <LoginModals
        isOpen={loginModalOpen}
        onHide={() => setLoginModalOpen(false)}
        sourceID="header"
      />
    </MainBarWrapper>
  )
}
